/**
 * Meilenstein-Detail — eigene Route in der Form der Vorlage.
 *
 * Kopf mit Kennung und Frist, Titel in 40, das Zahlenband über der
 * Tintenlinie, Anteile nach Zustandsgruppen, die Abarbeitung als
 * Wochenbalken aus den echten Fristen, die Aufgaben des Meilensteins und
 * rechts die Tafel mit dem Meilenstein selbst.
 *
 * Bewusst ohne neue Serverprozedur: `meilensteine.liste` ist auf der
 * Projektseite ohnehin schon geladen (React Query hält sie im Zwischenspeicher
 * vor), diese Seite sucht darin nur die passende Zeile. Kein zweiter Weg zu
 * denselben Daten.
 */

import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { Leerzustand } from "@/components/Leerzustand";
import { Planabhaengigkeiten } from "@/components/Planabhaengigkeiten";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Kennzahl } from "@/components/entwurf/Kennzahl";
import { Zahlenband } from "@/components/entwurf/Zahlenband";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Kennung, Zeilentitel } from "@/components/entwurf/Zeile";
import { Zustandspunkt } from "@/components/entwurf/Zustandspunkt";
import { Fortschrittslinie } from "@/components/entwurf/Fortschrittslinie";
import { verweisKlassen } from "@/components/entwurf/Verweis";
import {
  useMeilensteine,
  useMeilensteinFortschritt,
  useProjekt,
  useProjektVerzeichnis,
  useAufgabenFuerProjekte,
  useArbeitspakete,
} from "@/lib/haken";
import { leererFilter } from "@/lib/filter";
import { fehlerMeldung } from "@/lib/haken";
import { api } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  ZUSTAND,
  zustandAusGruppe,
  istAbgeschlossen,
} from "@/lib/entwurf-zustaende";
import { cx, focusRing } from "@/lib/utils";

function tageBis(wert: string | Date | null | undefined): number | null {
  if (!wert) return null;
  return Math.max(
    0,
    Math.ceil(
      (new Date(wert).getTime() - Date.now()) / (24 * 60 * 60 * 1_000),
    ),
  );
}

/** Kalenderwoche (ISO) eines Datums. */
function kalenderwoche(wert: Date): number {
  const d = new Date(wert);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const woche1 = new Date(d.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(
      ((d.getTime() - woche1.getTime()) / 86_400_000 -
        3 +
        ((woche1.getDay() + 6) % 7)) /
        7,
    )
  );
}

export function MeilensteinDetail() {
  const { projektId, meilensteinId } = useParams({
    from: "/_app/projekte/$projektId/meilensteine/$meilensteinId",
  });
  const client = useQueryClient();

  const projekt = useProjekt(projektId);
  const meilensteine = useMeilensteine(projektId);
  const fortschritt = useMeilensteinFortschritt(meilensteinId);
  const aufgaben = useAufgabenFuerProjekte([projektId], leererFilter());
  const arbeitspakete = useArbeitspakete(projektId, meilensteinId);
  const verzeichnis = useProjektVerzeichnis(projektId);

  const [fristOffen, setzeFristOffen] = useState(false);
  const [fristWert, setzeFristWert] = useState("");
  const [fristFehler, setzeFristFehler] = useState<ReturnType<typeof fehlerMeldung> | null>(null);
  const [zuordnenOffen, setzeZuordnenOffen] = useState(false);
  const [zuordnenId, setzeZuordnenId] = useState<string | null>(null);
  const [zuordnenPaketId, setzeZuordnenPaketId] = useState<string | null>(null);
  const [paketOffen, setzePaketOffen] = useState(false);
  const [paketName, setzePaketName] = useState("");
  const [archivOffen, setzeArchivOffen] = useState(false);

  const fristAendern = useMutation({
    mutationFn: (faelligAm: Date) =>
      api.meilensteine.aendern.mutate({
        id: meilensteinId,
        faelligAm,
        erwarteteVersion: m?.version ?? 1,
      }),
    onSuccess: async () => {
      setzeFristOffen(false);
      await Promise.all([
        meilensteine.refetch(),
        client.invalidateQueries({
          queryKey: ["cubic", "meilensteine"],
        }),
      ]);
    },
    onError: (fehler) => setzeFristFehler(fehlerMeldung(fehler)),
  });
  /*
   * Ein Schalter, kein Bearbeiten: `wiederherstellen` verlangt keine Version,
   * `archivieren` schon — dort kann sich zwischen Anzeige und Klick etwas
   * geändert haben, das man nicht ungefragt überschreiben will.
   */
  const archivieren = useMutation({
    mutationFn: () =>
      m?.archiviertAm
        ? api.meilensteine.wiederherstellen.mutate({ id: meilensteinId })
        : api.meilensteine.archivieren.mutate({
            id: meilensteinId,
            erwarteteVersion: m?.version ?? 1,
          }),
    onSuccess: async () => {
      setzeArchivOffen(false);
      await Promise.all([
        meilensteine.refetch(),
        aufgaben.refetch(),
        client.invalidateQueries({ queryKey: ["cubic", "meilensteine"] }),
      ]);
    },
    onError: (fehler) => setzeFristFehler(fehlerMeldung(fehler)),
  });

  const zuordnen = useMutation({
    mutationFn: (aufgabeId: string) => {
      const aufgabe = aufgaben.data?.find((eintrag) => eintrag.id === aufgabeId);
      if (!aufgabe) {
        throw new Error("Die Aufgabe ist nicht mehr vorhanden.");
      }
      return api.aufgaben.aendern.mutate({
        id: aufgabeId,
        arbeitspaketId: zuordnenPaketId,
        erwarteteVersion: aufgabe.version,
      });
    },
    onSuccess: async () => {
      setzeZuordnenOffen(false);
      setzeZuordnenId(null);
      setzeZuordnenPaketId(null);
      await Promise.all([aufgaben.refetch(), arbeitspakete.refetch()]);
    },
    onError: (fehler) => setzeFristFehler(fehlerMeldung(fehler)),
  });

  const paketAnlegen = useMutation({
    mutationFn: () =>
      api.arbeitspakete.anlegen.mutate({
        projektId,
        meilensteinId,
        name: paketName.trim(),
      }),
    onSuccess: async () => {
      setzePaketName("");
      setzePaketOffen(false);
      await arbeitspakete.refetch();
    },
    onError: (fehler) => setzeFristFehler(fehlerMeldung(fehler)),
  });

  /*
   * Die abgeleiteten Werte stehen **vor** den Wächtern.
   *
   * Sie hingen vorher hinter `if (isLoading) return` und hinter dem
   * Leerzustand — mit `useMemo` darin. Damit rief die Komponente im Ladezustand
   * zwei Haken weniger auf als danach, und React brach den Meilensteinbereich
   * beim ersten Datensatz mit „Rendered more hooks than during the previous
   * render" ab. Keiner der beiden Werte braucht `p` oder `m`; sie lassen sich
   * ohne Umbau nach oben ziehen.
   */
  const zustandsListe = verzeichnis.data?.zustaende ?? [];
  const zugehoerig = (aufgaben.data ?? []).filter(
    (a) => a.meilensteinId === meilensteinId && !a.archiviertAm,
  );
  const pakete = arbeitspakete.data ?? [];

  /* Anteile nach Zustandsgruppen — echte Verteilung der Aufgaben. */
  const anteile = useMemo(() => {
    const zaehlen = {
      erledigt: 0,
      abnahme: 0,
      in_arbeit: 0,
      offen: 0,
    };
    for (const a of zugehoerig) {
      const zustand = zustandsListe.find((z) => z.id === a.zustandId);
      const gruppe = zustand?.gruppe ?? "offen";
      if (gruppe === "erledigt" || gruppe === "verworfen") zaehlen.erledigt += 1;
      else if (gruppe === "abnahme") zaehlen.abnahme += 1;
      else if (gruppe === "in_arbeit") zaehlen.in_arbeit += 1;
      else zaehlen.offen += 1;
    }
    return zaehlen;
  }, [zugehoerig, zustandsListe]);

  /* Abarbeitung: Wochenbalken aus den Fristen der Aufgaben. */
  const wochen = useMemo(() => {
    if (zugehoerig.length === 0) return [];
    const wochenMap = new Map<number, { gesamt: number; fertig: number }>();
    for (const a of zugehoerig) {
      if (!a.faelligAm) continue;
      const kw = kalenderwoche(new Date(a.faelligAm));
      const zustand = zustandsListe.find((z) => z.id === a.zustandId);
      const fertig = zustand ? istAbgeschlossen(zustand.gruppe) : false;
      const eintrag = wochenMap.get(kw) ?? { gesamt: 0, fertig: 0 };
      eintrag.gesamt += 1;
      if (fertig) eintrag.fertig += 1;
      wochenMap.set(kw, eintrag);
    }
    return [...wochenMap.entries()]
      .sort(([a], [b]) => a - b)
      .map(([kw, w]) => ({
        kw,
        anteil: w.gesamt === 0 ? 0 : Math.round((w.fertig / w.gesamt) * 100),
        gesamt: w.gesamt,
        fertig: w.fertig,
      }));
  }, [zugehoerig, zustandsListe]);

  if (projekt.isLoading || meilensteine.isLoading || arbeitspakete.isLoading) {
    return <Mikrolabel grad={12}>Lade Meilenstein …</Mikrolabel>;
  }

  const p = projekt.data;
  const m = meilensteine.data?.find((x) => x.id === meilensteinId);

  if (!p || !m) {
    return (
      <Leerzustand
        titel="Diesen Meilenstein gibt es nicht."
        hinweis="Er ist nicht sichtbar, archiviert, oder die Adresse stimmt nicht."
        aktionLabel="Zu den Meilensteinen"
        onAktion={() => {
          globalThis.location.assign(
            `/projekte/${projektId}?reiter=meilensteine`,
          );
        }}
      />
    );
  }

  const kennung = `${p.kuerzel}-MS${m.nummer}`;
  const tage = tageBis(m.faelligAm);
  const ueberfaellig = tage === 0;

  const wert = fortschritt.data
    ? fortschritt.data.gesamt > 0
      ? Math.round((fortschritt.data.erledigt / fortschritt.data.gesamt) * 100)
      : 0
    : 0;

  const anteilGesamt = zugehoerig.length === 0 ? 0 : wert;

  async function fristSpeichern() {
    setzeFristFehler(null);
    if (!fristWert) return;
    try {
      await fristAendern.mutateAsync(new Date(`${fristWert}T12:00:00.000Z`));
      setzeFristWert("");
    } catch (e) {
      setzeFristFehler(fehlerMeldung(e));
    }
  }

  async function zuordnenSpeichern() {
    setzeFristFehler(null);
    if (!zuordnenId) return;
    try {
      await zuordnen.mutateAsync(zuordnenId);
    } catch (e) {
      setzeFristFehler(fehlerMeldung(e));
    }
  }

  const offeneAufgaben = zugehoerig.filter((a) => {
    const zustand = zustandsListe.find((z) => z.id === a.zustandId);
    return zustand ? !istAbgeschlossen(zustand.gruppe) : true;
  }).length;

  return (
    /* Eigener Rahmen, Tafel bündig an der Kante — siehe `lib/rahmen`. */
    <div className="flex min-h-full flex-wrap items-stretch">
      <div className="min-w-[300px] flex-1 px-4 pb-24 pt-8 sm:px-6 lg:pb-24 lg:pl-9 lg:pr-12 lg:pt-14">
        <div className="w-full max-w-[720px]">
          {fristFehler ? (
            <div className="mb-4">
              <Fehlerzeile meldung={fristFehler} />
            </div>
          ) : null}

          <Link
            to="/projekte/$id"
            params={{ id: projektId }}
            search={{ reiter: "meilensteine" }}
            className={verweisKlassen({ grad: 12 })}
          >
            ← Alle Meilensteine
          </Link>

          {/* Statuszeile: Kennung, Punkt, Frist. */}
          <div className="mt-[22px] flex flex-wrap items-center gap-3">
            <Mikrolabel grad={12.5}>{kennung}</Mikrolabel>
            <span className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className={cx(
                  "size-[7px] flex-none",
                  ueberfaellig ? "bg-akzent" : "bg-tinte3",
                )}
              />
              <Mikrolabel grad={11.5}>
                {ueberfaellig
                  ? "überfällig"
                  : tage != null
                    ? `Frist in ${tage} ${tage === 1 ? "Tag" : "Tagen"}`
                    : "läuft"}
              </Mikrolabel>
            </span>
          </div>

          <h1 className="mt-3 text-[40px] font-extralight leading-[1.1] tracking-[-0.034em] text-tinte">
            {m.name}
          </h1>
          {m.beschreibung ? (
            <p className="mt-4 max-w-[58ch] text-[15.5px] font-light leading-[1.7] text-tinte2">
              {m.beschreibung}
            </p>
          ) : null}

          {/* Zahlenband. */}
          <Zahlenband className="mt-[30px]">
            <Kennzahl
              label="Tage bis Frist"
              grad={56}
              wert={tage != null ? String(tage) : "—"}
              ton={ueberfaellig || (tage != null && tage <= 7) ? "akzent" : undefined}
            />
            <Kennzahl
              label="erledigt"
              grad={56}
              wert={
                fortschritt.data
                  ? `${fortschritt.data.erledigt} von ${fortschritt.data.gesamt}`
                  : "—"
              }
            />
            <Kennzahl
              label="offen"
              grad={56}
              wert={String(offeneAufgaben)}
            />
          </Zahlenband>

          {/* Anteile. */}
          <div className="mt-13">
            <Rubrik anzahl={`${anteilGesamt}%`}>Anteile</Rubrik>
            <div className="mt-3 flex h-[10px] w-full overflow-hidden shadow-[inset_0_0_0_1px_var(--linie3)]">
              {(
                [
                  ["erledigt", anteile.erledigt, "var(--tinte)"],
                  ["abnahme", anteile.abnahme, "oklch(0.9 0.06 195)"],
                  ["in_arbeit", anteile.in_arbeit, "oklch(0.94 0.03 195)"],
                  ["offen", anteile.offen, "var(--wisch)"],
                ] as const
              ).map(([name, anzahl, farbe]) =>
                anzahl === 0 ? null : (
                  <span
                    key={name}
                    className="block h-full"
                    style={{
                      width: `${(anzahl / Math.max(zugehoerig.length, 1)) * 100}%`,
                      background: farbe,
                    }}
                  />
                ),
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-[22px] gap-y-1 font-mono text-[10.5px] text-tinte4">
              <span className="flex items-center gap-2">
                <span className="size-2 bg-tinte" /> erledigt {anteile.erledigt}
              </span>
              <span className="flex items-center gap-2">
                <span className="size-2" style={{ background: "oklch(0.9 0.06 195)" }} /> Abnahme {anteile.abnahme}
              </span>
              <span className="flex items-center gap-2">
                <span className="size-2" style={{ background: "oklch(0.94 0.03 195)" }} /> in Arbeit {anteile.in_arbeit}
              </span>
              <span className="flex items-center gap-2">
                <span className="size-2 bg-wisch" /> offen {anteile.offen}
              </span>
            </div>
          </div>

          {/* Abarbeitung. */}
          {wochen.length > 0 ? (
            <div className="mt-13">
              <Rubrik>Wochen</Rubrik>
              <div className="mt-3 flex h-[42px] items-end gap-[5px]">
                {wochen.map((w) => (
                  <div key={w.kw} className="flex h-full flex-1 flex-col justify-end">
                    <span className="block h-[6px] w-full bg-linie2">
                      <span
                        className="block h-full bg-tinte"
                        style={{ width: `${w.anteil}%` }}
                      />
                    </span>
                    <span className="mt-1 text-center font-mono text-[9.5px] text-tinte4">
                      KW {w.kw}
                    </span>
                  </div>
                ))}
              </div>
              <Mikrolabel grad={10.5} className="mt-2">
                Fälligkeiten der Aufgaben, nicht Kapazität
              </Mikrolabel>
            </div>
          ) : null}

          {/* Arbeitspakete mit Aufgaben und genau einer Unteraufgabenebene. */}
          <div className="mt-13">
            <Rubrik
              anzahl={
                pakete.length +
                " " +
                (pakete.length === 1 ? "Arbeitspaket" : "Arbeitspakete")
              }
            >
              Arbeitspakete
            </Rubrik>
            {pakete.map((paket) => {
              const paketAufgaben = zugehoerig.filter(
                (aufgabe) => aufgabe.arbeitspaketId === paket.id,
              );
              const wurzeln = paketAufgaben.filter(
                (aufgabe) => !aufgabe.elternId,
              );
              return (
                <div key={paket.id} className="border-b border-linie py-4">
                <Link
                  to="/projekte/$projektId/arbeitspakete/$arbeitspaketId"
                  params={{ projektId, arbeitspaketId: paket.id }}
                  className={cx(
                    "flex items-center gap-3 transition-colors duration-150 ease-aus hover:text-akzent",
                    focusRing,
                  )}
                >
                  <Kennung>{p.kuerzel + "-AP" + paket.nummer}</Kennung>
                  <Zeilentitel titel={paket.name}>{paket.name}</Zeilentitel>
                  <Mikrolabel grad={10.5}>{wurzeln.length} Aufgaben</Mikrolabel>
                </Link>
                <div className="ml-5 mt-2 border-l border-linie pl-4">
                  {wurzeln.map((aufgabe) => {
                    const zustand = zustandsListe.find(
                      (eintrag) => eintrag.id === aufgabe.zustandId,
                    );
                    const gruppe = zustand?.gruppe ?? "offen";
                    const kinder = paketAufgaben.filter(
                      (kind) => kind.elternId === aufgabe.id,
                    );
                    return (
                      <div
                        key={aufgabe.id}
                        className="border-b border-linie py-2 last:border-b-0"
                      >
                        <Link
                          to="/aufgaben/$id"
                          params={{ id: aufgabe.id }}
                          className="flex items-center gap-3 hover:text-akzent"
                        >
                          <Zustandspunkt
                            bild={ZUSTAND[zustandAusGruppe(gruppe)]}
                          />
                          <Kennung>
                            {p.kuerzel + "-" + aufgabe.nummer}
                          </Kennung>
                          <span className="min-w-0 flex-1 truncate text-[13.5px]">
                            {aufgabe.titel}
                          </span>
                        </Link>
                        {kinder.map((kind) => (
                          <Link
                            key={kind.id}
                            to="/aufgaben/$id"
                            params={{ id: kind.id }}
                            className="ml-7 mt-1.5 flex items-center gap-2 text-[12.5px] font-light text-tinte3 hover:text-akzent"
                          >
                            <span aria-hidden="true">↳</span>
                            <Kennung>{p.kuerzel + "-" + kind.nummer}</Kennung>
                            <span className="truncate">{kind.titel}</span>
                          </Link>
                        ))}
                      </div>
                    );
                  })}
                  {wurzeln.length === 0 ? (
                    <Mikrolabel grad={10.5} className="block py-2">
                      Noch keine Aufgaben.
                    </Mikrolabel>
                  ) : null}
                </div>
                </div>
              );
            })}
            {pakete.length === 0 ? (
              <div className="border-b border-linie py-5">
                <Mikrolabel grad={11.5}>
                  Lege zuerst ein Arbeitspaket unter diesem Meilenstein an.
                </Mikrolabel>
              </div>
            ) : null}
            {zugehoerig.some((aufgabe) => !aufgabe.arbeitspaketId) ? (
              <div className="border-b border-warn py-3 text-[12.5px] text-warn">
                Ältere Direktzuordnungen ohne Arbeitspaket sind noch vorhanden.
                Ordne sie unten einem Arbeitspaket zu.
              </div>
            ) : null}
          </div>

          <Planabhaengigkeiten
            objekt={{ art: "meilenstein", id: meilensteinId }}
            kannAendern={!Boolean(m.archiviertAm)}
          />

          {/* Aktionen. */}
          <div className="mt-10 flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={() => setzeFristOffen(true)}
              className={cx(
                "flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] shadow-[inset_0_0_0_1px_var(--tinte4)] transition-colors duration-150 ease-aus hover:bg-wisch",
                focusRing,
              )}
            >
              Frist verschieben
            </button>
            <button
              type="button"
              onClick={() => setzePaketOffen(true)}
              disabled={Boolean(m.archiviertAm)}
              className={cx(
                "flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] shadow-[inset_0_0_0_1px_var(--tinte4)] transition-colors duration-150 ease-aus hover:bg-wisch disabled:cursor-not-allowed disabled:opacity-40",
                focusRing,
              )}
            >
              Arbeitspaket anlegen
            </button>
            <button
              type="button"
              onClick={() => {
                setzeZuordnenPaketId(pakete[0]?.id ?? null);
                setzeZuordnenOffen(true);
              }}
              disabled={Boolean(m.archiviertAm) || pakete.length === 0}
              className={cx(
                "flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] shadow-[inset_0_0_0_1px_var(--tinte4)] transition-colors duration-150 ease-aus hover:bg-wisch disabled:cursor-not-allowed disabled:opacity-40",
                focusRing,
              )}
            >
              Aufgabe einplanen
            </button>
            {m.archiviertAm ? (
              <button
                type="button"
                onClick={() => archivieren.mutate()}
                disabled={archivieren.isPending}
                className={cx(
                  "flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] shadow-[inset_0_0_0_1px_var(--tinte4)] transition-colors duration-150 ease-aus hover:bg-wisch disabled:opacity-40",
                  focusRing,
                )}
              >
                {archivieren.isPending ? "Holt zurück …" : "Wiederherstellen"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setzeArchivOffen(true)}
                className={cx(
                  "ml-auto flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] text-warn shadow-[inset_0_0_0_1px_var(--warn)] transition-colors duration-150 ease-aus hover:bg-warnflaeche",
                  focusRing,
                )}
              >
                Archivieren
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Rechte Tafel. */}
      <div className="min-w-[252px] flex-[0_1_316px] bg-tafel px-[26px] pb-24 pt-11 text-tafel-tinte">
        <div className="border-b border-tafel-linie pb-3.5 font-mono text-[12.5px] font-medium text-tafel-tinte">
          Meilenstein
        </div>
        {[
          ["Kennung", kennung],
          ["Name", m.name],
          [
            "Frist",
            m.faelligAm
              ? new Date(m.faelligAm).toLocaleDateString("de-DE")
              : "—",
          ],
          [
            "Zustand",
            ueberfaellig ? "überfällig" : tage != null ? "läuft" : "—",
          ],
        ].map(([label, wert]) => (
          <div
            key={label}
            className="flex items-baseline gap-3.5 border-b border-tafel-wisch py-3"
          >
            <span className="w-[84px] flex-none font-mono text-[11.5px] text-tafel-tinte3">
              {label}
            </span>
            <span className="min-w-0 flex-1 text-[13.5px] font-light text-tafel-tinte">
              {wert}
            </span>
          </div>
        ))}

        <div className="mt-10 border-b border-tafel-linie pb-3.5 font-mono text-[12.5px] font-medium text-tafel-tinte">
          Fortschritt
        </div>
        <div className="pt-4">
          <Fortschrittslinie
            anteil={anteilGesamt}
            label="erledigte Aufgaben"
          />
          <Mikrolabel grad={11} className="mt-2 text-tafel-tinte3">
            {fortschritt.data
              ? `${fortschritt.data.erledigt} von ${fortschritt.data.gesamt} Aufgaben`
              : "—"}
          </Mikrolabel>
        </div>
      </div>

      {/*
        * Archivieren räumt die verbindliche Hierarchie bewusst von unten auf:
        * Arbeitspakete werden archiviert, ihre Aufgaben landen im Rückstand.
        * Beim Wiederherstellen werden Zuordnungen nicht geraten.
        */}
      <Dialog open={archivOffen} onOpenChange={setzeArchivOffen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Meilenstein archivieren</DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-relaxed">
            {pakete.length === 0 && zugehoerig.length === 0
              ? "Der Meilenstein verschwindet aus der Liste. Zurückholen geht jederzeit."
              : `${pakete.length} Arbeitspaket${pakete.length === 1 ? "" : "e"} ${pakete.length === 1 ? "wird" : "werden"} dabei archiviert und ${zugehoerig.length} Aufgabe${zugehoerig.length === 1 ? "" : "n"} ${zugehoerig.length === 1 ? "wird" : "werden"} in den Rückstand verschoben. Beim Zurückholen werden diese Zuordnungen nicht automatisch wiederhergestellt.`}
          </p>
          {fristFehler ? <Fehlerzeile meldung={fristFehler} /> : null}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setzeArchivOffen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => archivieren.mutate()}
              disabled={archivieren.isPending}
            >
              {archivieren.isPending ? "Archiviert …" : "Archivieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fristOffen} onOpenChange={setzeFristOffen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Frist verschieben</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div>
              <Label htmlFor="meilenstein-frist">Neue Frist</Label>
              <Input
                id="meilenstein-frist"
                type="date"
                className="mt-1"
                value={fristWert}
                onChange={(e) => setzeFristWert(e.target.value)}
              />
            </div>
            {fristFehler ? <Fehlerzeile meldung={fristFehler} /> : null}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => setzeFristOffen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => void fristSpeichern()}
              disabled={!fristWert || fristAendern.isPending}
            >
              Verschieben
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verbindliche Ebene unter dem Meilenstein anlegen. */}
      <Dialog open={paketOffen} onOpenChange={setzePaketOffen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Arbeitspaket anlegen</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <Label htmlFor="arbeitspaket-name">Name</Label>
            <Input
              id="arbeitspaket-name"
              className="mt-1"
              value={paketName}
              onChange={(ereignis) => setzePaketName(ereignis.target.value)}
              placeholder="Zum Beispiel Anmeldung und Rollen"
              autoFocus
            />
            <p className="mt-2 text-[12.5px] font-light leading-relaxed text-tinte3">
              Aufgaben werden immer über ein Arbeitspaket in diesen
              Meilenstein eingeplant.
            </p>
            {fristFehler ? <Fehlerzeile meldung={fristFehler} /> : null}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => setzePaketOffen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => paketAnlegen.mutate()}
              disabled={!paketName.trim() || paketAnlegen.isPending}
            >
              {paketAnlegen.isPending ? "Legt an …" : "Anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wurzelaufgabe in ein Arbeitspaket einplanen oder dorthin verschieben. */}
      <Dialog open={zuordnenOffen} onOpenChange={setzeZuordnenOffen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Aufgabe einplanen</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <Label>Arbeitspaket</Label>
            <div className="mt-1 grid gap-1">
              {pakete.map((paket) => {
                const gewaehlt = zuordnenPaketId === paket.id;
                return (
                  <button
                    key={paket.id}
                    type="button"
                    onClick={() => setzeZuordnenPaketId(paket.id)}
                    className={cx(
                      "flex w-full items-center gap-3 px-2 py-2 text-left shadow-[inset_0_0_0_1px_var(--linie2)] transition-colors hover:bg-wisch",
                      gewaehlt && "bg-wisch shadow-[inset_3px_0_0_var(--tinte)]",
                      focusRing,
                    )}
                  >
                    <span className="font-mono text-[11.5px] text-tinte4">
                      {p.kuerzel}-AP{paket.nummer}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13.5px]">
                      {paket.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <Label className="mt-5 block">Wurzelaufgabe</Label>
          <div className="mt-4 max-h-80 space-y-1 overflow-y-auto">
            {(aufgaben.data ?? [])
              .filter((a) => !a.archiviertAm && !a.elternId)
              .map((a) => {
                const gewaehlt = zuordnenId === a.id;
                const aktuellesPaket = pakete.find(
                  (paket) => paket.id === a.arbeitspaketId,
                );
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setzeZuordnenId(gewaehlt ? null : a.id)}
                    className={cx(
                      "flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-wisch",
                      gewaehlt && "bg-wisch",
                    )}
                  >
                    <span className="font-mono text-[11.5px] text-tinte4">
                      {p.kuerzel}-{a.nummer}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13.5px] text-tinte">
                      {a.titel}
                    </span>
                    {aktuellesPaket ? (
                      <Mikrolabel grad={10.5}>{aktuellesPaket.name}</Mikrolabel>
                    ) : a.meilensteinId ? (
                      <Mikrolabel grad={10.5}>alte Direktzuordnung</Mikrolabel>
                    ) : null}
                  </button>
                );
              })}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => setzeZuordnenOffen(false)}>
              Schließen
            </Button>
            <Button
              onClick={() => void zuordnenSpeichern()}
              disabled={!zuordnenId || !zuordnenPaketId || zuordnen.isPending}
            >
              Einplanen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
